import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  CryptotokenlistActions,
  createCryptotokenlistDataEffects,
  createCryptotokenlistDrilldownEffects,
  cryptotokenlistFeature,
} from './cryptotokenlist.feature';
import { CryptoToken } from 'src/app/shared/entities/crypto-token';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { ToastrService } from 'src/app/shared/services/toastr/toastr-service';
import { CryptotokenDataService } from 'src/app/shared/store/features/cryptotoken-data/cryptotoken-data.service';
import { CryptotokenListColId } from '../cryptotokenlist.types';
import { booleanToYesNo } from 'src/app/shared/util/yes-no';
import { stringComparator } from 'src/app/shared/util/comparators';
import { extractErrorMessage } from 'src/app/shared/util/extract-error-message';
import { GridDataSourceState } from 'src/app/shared/store/grid-data/grid-data-source-state';
import { pageDataToGridData } from 'src/app/shared/store/grid-data/page-data-to-grid-data';

@Injectable()
export class CryptotokenlistEffects {
  private action$ = inject(Actions);
  private router = inject(Router);
  private store = inject(Store);
  private langService = inject(LangService);
  private labels = this.langService.lang.features.calist;
  private toastrService = inject(ToastrService);
  private cryptotokenDataService = inject(CryptotokenDataService);

  private gridDataSourceState$: Observable<GridDataSourceState<CryptoToken>> =
    pageDataToGridData<CryptoToken>({
      filterChecks: {
        [CryptotokenListColId.tokenName]: (cryptoToken, filter) =>
          !!cryptoToken.tokenName?.toLowerCase().includes(filter.toLowerCase()),
        [CryptotokenListColId.active]: (cryptoToken, filter) =>
          booleanToYesNo(!!cryptoToken.active) === filter,
        [CryptotokenListColId.autoActivation]: (cryptoToken, filter) =>
          booleanToYesNo(!!cryptoToken.autoActivation) === filter,
      },
      comparisons: {
        [CryptotokenListColId.tokenName]: stringComparator(
          cryptoToken => cryptoToken.tokenName ?? '',
        ),
      },
    })(
      this.cryptotokenDataService.state$,
      this.store.select(cryptotokenlistFeature.selectGridDataParams),
    );

  private dataEffects = createCryptotokenlistDataEffects(
    this.action$,
    this.store,
    this.gridDataSourceState$,
    this.router,
    {
      requestData: () => this.cryptotokenDataService.ensure(),
      refreshData: () => {
        /* Do nothing: refresh not supported */
      },
    },
  );

  private drilldownEffects = createCryptotokenlistDrilldownEffects(
    this.action$,
    this.router,
  );

  dataRequest$ = this.dataEffects.dataRequest$;
  dataUpdated$ = this.dataEffects.dataUpdated$;
  navigated$ = this.dataEffects.navigated$;
  refresh$ = this.dataEffects.refresh$;
  applyFilter$ = this.dataEffects.applyFilter$;
  applySorting$ = this.dataEffects.applySorting$;
  drilldown$ = this.drilldownEffects.drilldown$;
  backfromDrilldown$ = this.drilldownEffects.backFromDrilldown$;

  toastOnError$ = createEffect(
    () =>
      this.action$.pipe(
        ofType(CryptotokenlistActions.dataLoadFailure),
        tap(action =>
          this.toastrService.error(
            extractErrorMessage(action.error) ||
              this.labels.toasts.fetchingError,
          ),
        ),
      ),
    { dispatch: false },
  );
}
